import { makeAutoObservable } from 'mobx'
import { showToast } from 'shared/ui'
import type { IEntitiesStore } from 'entities/EntitiesExport'

export class CallPopUpOnHoldStore {
  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get callStore() {
    return this._entities.callStore
  }

  private get _entries() {
    return this.callStore.heldEntries
  }

  get items() {
    return this._entries.map((entry) => entry.callContact)
  }

  get hasItems() {
    return this._entries.length > 0
  }

  get count() {
    return this._entries.length
  }

  get loading() {
    return false
  }

  get resumePending() {
    return this.callStore.isResumePending
  }

  handleResume = async (callSid: string) => {
    if (this.callStore.getEntry(callSid)?.isEndPending) return

    const primary = this.callStore.primaryEntry
    if (primary && primary.isOpen && !primary.isHold && primary.callSid !== callSid) {
      const held = await this.callStore.holdCall(primary.callSid)
      if (!held) {
        // If holding the active call failed because its leg is already gone
        // (control error → dropped from the map), don't abort: the customer we
        // want to resume is still parked. Only bail when the active call is still
        // present, i.e. a genuine hold failure.
        const stillActive = this.callStore.primaryEntry?.callSid === primary.callSid
        if (stillActive) {
          showToast({
            type: 'error',
            title: "Couldn't put the active call on hold. Please try again.",
          })
          return
        }
      }
    }

    const response = await this.callStore.unholdCall(callSid)

    if (!response.ok) {
      showToast({
        type: 'error',
        title: "Couldn't resume the call. Please try again.",
      })
      return
    }

    if (response.mode === 'new_leg') {
      const originated = await this.callStore.originateResumeLeg(callSid, response.resumeToken)
      if (!originated) {
        showToast({
          type: 'error',
          title: "Couldn't resume the call. Please try again.",
        })
      }
    }
  }

  handleEnd = (callSid: string) => {
    void this.callStore.disconnectCall(callSid, true)
  }

  handleEndAll = () => {
    this._entries.forEach((entry) => void this.callStore.disconnectCall(entry.callSid, true))
  }
}
